/** Scoped CSS for the function-machine widget (injected via <style> in the container). */
export const STYLES = `
/* Container — tight padding, content determines bounds */
.fm {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 4px;
}
.fm--horizontal { flex-direction: row; flex-wrap: wrap; justify-content: center; }
.fm--vertical { flex-direction: column; }
/* Static inverse (params.inverse) — arrow flip only, no layout change */

/* Block (shared) */
.fm-block {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  min-height: 48px;
  padding: 10px 16px;
  box-sizing: border-box;
}

/* Value blocks — uniform sizing for input and output */
.fm-block--value {
  background: var(--neutral-0);
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  transition: border-color 0.2s, opacity 0.3s ease;
  width: 56px;
  height: 36px;
  min-width: 56px;
  min-height: 36px;
  padding: 6px 8px;
}
.fm-block--value.fm-block--error { border-color: var(--red-300); }
.fm-block--value--highlighted { border-color: var(--sky-500); }
.fm-block--function--highlighted { background-color: var(--sky-100) !important; }

.fm-block__label {
  position: absolute;
  top: -16px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  font-size: 10px;
  font-weight: 500;
  color: var(--neutral-300);
  letter-spacing: 0.02em;
  user-select: none;
}

.fm-block__input {
  color: var(--neutral-800);
  text-align: center;
  font-weight: 600;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  height: 100%;
}
.fm-block__input:focus {
  border-radius: 6px;
  box-shadow: 0 0 0 2px var(--sky-300);
}
.fm-block__input::-webkit-inner-spin-button,
.fm-block__input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.fm-block__value {
  color: var(--neutral-800);
  font-weight: 600;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  transition: opacity 0.3s ease;
}
.fm-block__value--error {
  color: var(--red-500);
  font-size: 13px;
  font-weight: 500;
}
.fm-block__value--pending { opacity: 0.3; }

/* Function blocks (badge-style) — neutral by default, colored only during animation */
.fm-block--function {
  background-color: var(--neutral-50);
  color: var(--neutral-600);
  border: none;
  border-radius: 14px;
  padding: 20px 32px 20px 24px;
  min-width: 110px;
  transition: background-color 0.3s ease, color 0.3s ease;
}
.fm-block--function--highlighted {
  background-color: var(--block-highlight-bg) !important;
  color: var(--block-highlight-text) !important;
}
.fm-block--value--highlighted { border-color: var(--highlight-color, var(--sky-500)); }

.fm-block__gear {
  position: absolute;
  top: 8px;
  right: 10px;
  color: var(--sky-500);
  opacity: 0.45;
  animation: fm-gear-idle 6s linear infinite;
  line-height: 0;
}
.fm-block__gear--spinning {
  opacity: 0.8;
  animation: fm-gear-spin 0.4s linear infinite;
}

.fm-block__expression {
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
}

/* Transform blocks — subtle by default, matching function block style */
.fm-block--transform {
  background-color: var(--neutral-50);
  color: var(--neutral-600);
  border: none;
  border-radius: 14px;
  padding: 20px 24px;
  transition: background-color 0.3s ease, color 0.3s ease;
}
.fm-block--transform--filled {
  background-color: var(--transform-fill, var(--neutral-400));
}
.fm-block--transform--filled .fm-block__operation { color: var(--neutral-0); }
.fm-block--transform--highlighted {
  background-color: var(--block-highlight-bg, var(--neutral-100)) !important;
}
.fm-block--transform--highlighted .fm-block__operation {
  color: var(--block-highlight-text, var(--neutral-700)) !important;
}
.fm-block__operation {
  color: var(--neutral-600);
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
}

/* Block wrapper (function/transform + run button) */
.fm-block-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

/* Run button — ExamDojo outline neutral style */
.fm-run-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid var(--sky-200);
  border-radius: 6px;
  background: var(--neutral-0);
  color: var(--sky-500);
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  white-space: nowrap;
  line-height: 1.4;
}
.fm-run-btn:hover:not(:disabled) {
  background: var(--sky-50);
  border-color: var(--sky-300);
  color: var(--sky-600);
}
.fm-run-btn:active:not(:disabled) {
  background: var(--sky-100);
  color: var(--sky-700);
}
.fm-run-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.fm-run-btn--global { padding: 4px 14px; font-size: 12px; }
.fm-run-btn svg { flex-shrink: 0; }

/* Global run */
.fm-global-run {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 2px;
}

/* Controls (invert toggle + run button) */
.fm-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding-top: 2px;
}

/* Invert toggle button */
.fm-invert-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid var(--neutral-300);
  border-radius: 6px;
  background: transparent;
  color: var(--neutral-500);
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  white-space: nowrap;
  line-height: 1.4;
}
.fm-invert-btn:hover:not(:disabled) {
  border-color: var(--invert-color, var(--neutral-400));
  color: var(--invert-color, var(--neutral-500));
  background: var(--invert-bg, var(--neutral-50));
}
.fm-invert-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.fm-invert-btn--active {
  border-color: var(--invert-color) !important;
  background: var(--invert-color) !important;
  color: var(--neutral-0) !important;
}
.fm-invert-btn--active:hover:not(:disabled) {
  background: var(--invert-hover, var(--invert-color)) !important;
  border-color: var(--invert-hover, var(--invert-color)) !important;
  color: var(--neutral-0) !important;
}
.fm-invert-btn svg { flex-shrink: 0; }

/* Arrows */
.fm-arrow {
  display: flex;
  align-items: center;
  color: var(--neutral-300);
  transition: color 0.4s ease;
  flex-shrink: 0;
}
.fm-arrow--animated { color: var(--arrow-color, var(--sky-500)); }
.fm-arrow__svg { width: 32px; height: 18px; }

.fm--vertical .fm-arrow__svg { transform: rotate(90deg); }
.fm--inverse .fm-arrow__svg { transform: scaleX(-1); }
.fm--inverse.fm--vertical .fm-arrow__svg { transform: rotate(90deg) scaleX(-1); }

/* Animations */
@keyframes fm-gear-idle {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes fm-gear-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
`;
